'use client'

// React Imports
import { useState } from 'react'

// Next Imports
import Link from 'next/link'
import { useParams, useRouter, useSearchParams } from 'next/navigation'

import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'

// Third-party Imports
import { valibotResolver } from '@hookform/resolvers/valibot'
import type { SubmitHandler } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'
import type { InferInput } from 'valibot'
import { custom, email, minLength, nonEmpty, object, pipe, string } from 'valibot'

import { signIn } from 'next-auth/react'

import { toast } from 'react-toastify'

import { CircularProgress } from '@mui/material'

import type { getDictionary } from '@/utils/getDictionary'

// Type Imports

import type { Locale } from '@/configs/i18n'

// Component Imports
import CustomTextField from '@core/components/mui/TextField'

// Util Imports
import { getLocalizedUrl } from '@/utils/i18n'

type Props = {
  dictionary: Awaited<ReturnType<typeof getDictionary>>
}

const Login = ({ dictionary }: Props) => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const { lang: locale } = useParams()

  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  type FormData = InferInput<typeof schema>

  const schema = object({
    email: pipe(string(), minLength(1, 'This field is required'), email('Email is invalid')),
    password: pipe(
      string(),
      nonEmpty(dictionary.auth.password_required),
      minLength(8, dictionary.auth.password_min_length),
      custom(
        value => typeof value === 'string' && /(?=.*[a-z])(?=.*[A-Z])(?=.*[\d\W])/.test(value),
        dictionary.auth.password_complexity
      )
    )
  })

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: valibotResolver(schema),
    defaultValues: {
      email: '',
      password: ''
    }
  })

  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    setIsLoading(true)

    const res = await signIn('credentials', {
      email: data.email,
      password: data.password,
      redirect: false
    })

    setIsLoading(false)

    if (res?.error || !res?.ok || res?.status === 401) {
      toast.error(res?.error || 'Authentication failed. Please check your credentials.')
    } else {
      toast.success(dictionary.auth.login_success)

      // const session = await getSession()

      // const role = session?.user?.role
      // const status = session?.user?.request_status

      const redirectURL = '/customer-experience'

      router.replace(getLocalizedUrl(searchParams.get('redirectTo') ?? redirectURL, locale as Locale))
    }
  }

  return (
    <div className='flex bs-full items-center justify-center flex-col gap-6'>
      <div className='z-[3] w-[280px] h-auto sm:w-[316.55px] sm:h-[60px]'>
        <img src='/images/logo.png' alt='Logo' className='w-full h-full' />
      </div>

      <div className='flex justify-center items-center bg-backgroundPaper w-[340px] sm:w-[384px] max-h-[436px] backdrop-blur-[15px] rounded-lg p-5 sm:p-6 z-10'>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1'>
            <h4 className='text-white text-xl sm:text-[24px] font-medium leading-[38px] text-left'>
              {dictionary.auth.welcome_digital_you} 👋🏻
            </h4>
            <p className='text-white text-sm sm:text-[15px] font-normal leading-[22px]'>
              {dictionary.auth.sign_in_prompt}
            </p>
          </div>
          <form
            noValidate
            autoComplete='off'
            action={() => {}}
            onSubmit={handleSubmit(onSubmit)}
            className='flex flex-col gap-6'
          >
            <Controller
              name='email'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomTextField
                  {...field}
                  autoFocus
                  fullWidth
                  type='email'
                  label={
                    <Typography className='text-white text-[13px] font-normal leading-[15px]'>
                      {' '}
                      {dictionary.auth.email}
                    </Typography>
                  }
                  placeholder={dictionary.auth.enter_email}
                  onChange={e => {
                    field.onChange(e.target.value)
                  }}
                  {...(errors.email && {
                    error: true,
                    helperText: errors?.email?.message
                  })}
                />
              )}
            />
            <Controller
              name='password'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomTextField
                  {...field}
                  fullWidth
                  label={
                    <Typography className='text-white text-[13px] font-normal leading-[15px]'>
                      {' '}
                      {dictionary.auth.password}
                    </Typography>
                  }
                  placeholder='············'
                  id='login-password'
                  type={isPasswordShown ? 'text' : 'password'}
                  onChange={e => {
                    field.onChange(e.target.value)
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            edge='end'
                            onClick={handleClickShowPassword}
                            onMouseDown={e => e.preventDefault()}
                          >
                            <i className={isPasswordShown ? 'tabler-eye' : 'tabler-eye-off'} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                  {...(errors.password && { error: true, helperText: errors.password.message })}
                />
              )}
            />
            <div className='flex justify-between items-center gap-x-3 gap-y-1 flex-wrap'>
              <FormControlLabel
                className='mr-0'
                control={<Checkbox defaultChecked />}
                label={
                  <Typography className='text-white text-[15px] font-normal leading-[22px]'>
                    {' '}
                    {dictionary.auth.remember_me}
                  </Typography>
                }
              />
              <Typography
                className='text-end text-[15px] font-normal leading-[22px]'
                fontFamily='Montserrat'
                color='white'
                component={Link}
                href={getLocalizedUrl('/forgot-password', locale as Locale)}
              >
                {dictionary.auth.forgot_password}
              </Typography>
            </div>
            <Button
              fullWidth
              variant='contained'
              type='submit'
              className='bg-gradient-custom shadow-none'
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={20} className='text-white' /> : dictionary.auth.login}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Login
